// interaction.js — pointer interaction: branch hover/prune + shape mode.
//
// Two mutually exclusive modes share the canvas (toggled by the Prune / Shape
// buttons; exactly one is active, Prune by default):
//   - prune mode: hovering highlights a branch; left-click prunes it.
//   - shape mode (toggled via toggleShapeMode): click a branch to select, drag
//     to bend it; a gizmo marks the grab point; right-click exits to prune.
//
// All interaction state (raycaster, mouse, hover/selection, drag flags) is
// private to this module. Scene-core singletons are read from the shared spine
// (app.scene / app.camera / app.controls / app.renderer / app.tree). Call
// initInteraction() once, after the renderer exists, to attach the listeners.

import * as THREE from 'three';
import { parameters, app } from './core.js';
import { playSfx } from './audio.js';
import { shedSnowIn } from './tree.js';

// Interaction-only state.
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
let hoveredBranch = null;   // a regular Branch under the cursor (partial-prune target)
let hoveredCutRing = null;  // ring it would be cut at (from Branch.cutRingForPoint)
let hoveredMain = null;     // a main-branch mesh under the cursor (partial-prune target)
let hoveredMainRing = null; // ring it would be cut at (0 = whole branch, via cutRingForMainBranch)
// Current edit mode: 'prune' (default), 'shape', or 'none' (view-only — orbit
// the camera, clicking does nothing). The Shape/Prune buttons each toggle their
// mode on; clicking the active one again ("Exit …") drops back to 'none'.
let mode = 'prune';
let selectedBranch = null;
let shapeStartPoint = null;
let isDragging = false;
let shapeGizmo = null;
let shapeDirty = false;     // a bend was applied this drag and isn't recorded yet
let snowShedThisGesture = false; // winter: snow already knocked off for this drag?
// Click-vs-drag disambiguation for the desktop grab. A grab that the player drags
// (button held + moved past SHAPE_DRAG_PX) auto-releases on mouseup; a grab they
// just click in place stays held for the click-again-to-release toggle.
let shapeGrabX = 0, shapeGrabY = 0; // pixel cursor position when the branch was grabbed
let shapeDidDrag = false;           // has the cursor moved past the drag threshold since grab?
const SHAPE_DRAG_PX = 8;            // movement beyond this (px) counts as a drag, not a click
let lastShapeRustle = 0;    // performance.now() of the last steady shaping rustle (throttle)
let lastShapeShake = 0;     // performance.now() of the last abrupt-shake hit (throttle)
let shakeDirX = 0, shakeDirY = 0; // previous shaping frame's normalized drag direction
let haveShakeDir = false;   // is shakeDir* valid? reset at the start of each drag
const SHAPE_RUSTLE_MS = 2880; // min gap between steady rustles so per-frame bends don't machine-gun
const SHAPE_RUSTLE_RATE = 0.7; // <1 slows + deepens the steady rustle for a calmer, drawn-out sway
const SHAPE_SHAKE_MS = 130;  // shorter cooldown so a rapid back-and-forth shake can re-trigger
// A "shake" is a sharp back-and-forth: the drag direction reverses frame-to-frame.
// (Per-frame drag is in NDC, only ever ~0–2.8, so a speed threshold is unusable —
// direction reversal is the reliable signal and needs no magic scale.)
const SHAPE_SHAKE_DOT = -0.4;     // dir·prevDir below this (≈ >113° reversal) counts as a shake
const SHAPE_SHAKE_MIN_NDC = 0.015; // ...but only if the move has real speed (ignore micro-jitter)
// Screen-space (NDC) cursor position at the previous shaping frame. shapeBranch()
// bends incrementally from the per-frame screen delta, so a bend always tracks
// the on-screen drag direction regardless of branch orientation or camera angle.
const lastShapeScreen = new THREE.Vector2();

// Scratch objects reused by shapeBranch() so the per-frame bend allocates nothing.
// _tipMain/_tipReg are representative local "tip" directions per branch type: main
// branches (Lathe) extend along local +Y; regular branches (a Bezier tube ending
// near (length, 0.2·length, 0)) extend mostly along local +X. shapeBranch probes
// how that tip moves on screen, so it must use the right axis for each.
const _tipMain = new THREE.Vector3(0, 1, 0);
const _tipReg = new THREE.Vector3(1, 0.2, 0);
const _tipWorld = new THREE.Vector3();
const _drag = new THREE.Vector2();   // per-frame screen-space drag delta (NDC)
const _s0 = new THREE.Vector2();
const _sY = new THREE.Vector2();
const _sZ = new THREE.Vector2();

// The orange shaping dot is glued to the spot on the branch where it was grabbed:
// gizmoTarget is the branch object it rides, gizmoLocal the grab point in that
// object's local space. Each shaping frame we re-derive its world position, so the
// dot tracks the branch as it bends (instead of drifting off toward the cursor).
let gizmoTarget = null;
const gizmoLocal = new THREE.Vector3();
const _gizmoWorld = new THREE.Vector3();

// Touch-gesture state (mobile). A short, near-stationary touch is a "tap";
// touchShaping marks a one-finger drag that's bending a branch.
let touchStartX = 0;
let touchStartY = 0;
let touchStartTime = 0;
let touchMoved = false;
let touchShaping = false;
const TAP_MOVE_PX = 10;
const TAP_TIME_MS = 300;

// Rage-tap-the-trunk shortcut (mobile). The trunk isn't a prunable branch, so
// poking it does nothing — which reads as "I want to edit this". A quick burst of
// taps clustered on the trunk pops open the Trunk bottom sheet (the controls they
// were after). Any non-trunk tap resets the burst.
const RAGE_TAP_COUNT = 3;        // trunk taps in a row to trigger
const RAGE_TAP_WINDOW_MS = 600;  // max gap between consecutive taps
const RAGE_TAP_RADIUS_PX = 70;   // taps must cluster in roughly the same spot
let rageTapCount = 0;
let rageTapTime = 0;
let rageTapX = 0;
let rageTapY = 0;

// Coarse pointer → phone/tablet; used to pick touch vs mouse instruction copy.
function isTouch() {
  return window.matchMedia('(pointer: coarse)').matches;
}

// Highlight an entire main-branch group (its wood plus every attached
// sub-branch and their foliage) red while hovered, so the player sees exactly
// what a whole-branch prune will remove. The main-branch wood glows via emissive
// (its prior value stashed per object for a precise restore); the sub-branches go
// through the shared doomed path (previewDoomed), which reddens their wood AND
// their foliage/twigs by color — emissive alone barely tints leaves/needles, so
// without this the red stops at the wood and never reaches the foliage.
function setMainBranchHighlight(mainMesh, on) {
  const group = mainMesh.userData.branchGroup;
  if (!group) return;
  group.children.forEach(obj => {
    const sub = obj.userData && obj.userData.branch;
    if (sub) {
      if (on) { if (!sub.isPruned) sub.previewDoomed(); }
      else sub.restoreDoomed();
      return;
    }
    // Main-branch wood (bark, interior sap-wood layer, end cap): emissive glow.
    const mat = obj.material;
    if (!mat || !mat.emissive) return;
    if (on) {
      if (obj.userData._origEmissive === undefined) obj.userData._origEmissive = mat.emissive.getHex();
      mat.emissive.setHex(0x661111);
    } else if (obj.userData._origEmissive !== undefined) {
      mat.emissive.setHex(obj.userData._origEmissive);
      delete obj.userData._origEmissive;
    }
  });
}

// Show the hover preview for a main branch: a cut at ring k reds out just the
// offcut (previewMainBranchCut), while k === 0 (cut at the base) reds the whole
// branch (setMainBranchHighlight). Paired with clearMainHover().
function applyMainHover(mesh, k) {
  hoveredMain = mesh;
  hoveredMainRing = k;
  const group = mesh.userData.branchGroup;
  if (k === 0) setMainBranchHighlight(mesh, true);
  else app.tree.previewMainBranchCut(group, k);
}

// Tear down whatever main-branch hover preview is showing.
function clearMainHover() {
  if (!hoveredMain) return;
  const group = hoveredMain.userData.branchGroup;
  if (hoveredMainRing === 0) setMainBranchHighlight(hoveredMain, false);
  else if (group) app.tree.clearMainBranchCutPreview(group);
  hoveredMain = null;
  hoveredMainRing = null;
}

function updateShapingInstructions(isShaping) {
  const instructions = document.getElementById('instructions');
  
  if (isShaping) {
    const msg = isTouch()
      ? 'SHAPING: Drag to bend • Lift finger to release'
      : 'SHAPING: Move mouse to bend • Click to release • Right-click to exit';
    instructions.innerHTML = msg + ' <span class="close-instructions">✕</span>';
    instructions.style.background = 'rgba(137, 112, 11, 0.9)'; // Yellow when actively shaping
  } else {
    const msg = isTouch()
      ? 'SHAPING MODE: Long tap & drag a branch to bend'
      : 'SHAPING MODE: Click to select branch to bend • Right-click to exit';
    instructions.innerHTML = msg + ' <span class="close-instructions">✕</span>';
    instructions.style.background = 'rgba(187, 87, 20, 0.9)'; // Red when in shaping mode but not actively shaping
  }
}

// Reflect the current mode on the two toggle buttons. Each button is "active"
// (lit + scaled) only when its mode is current, "inactive" (grayed) otherwise,
// and carries a small on/off status pinned to its right corner.
function refreshModeButtons() {
  const shapeButton = document.getElementById('shapeModeToggle');
  const pruneButton = document.getElementById('pruneModeToggle');
  const shaping = mode === 'shape';
  const pruning = mode === 'prune';

  // Keep the mode's own emoji + label; append an on/off chip (styled .btn-status,
  // pinned to the right corner) showing whether that mode is currently active.
  const status = (active) =>
    `<span class="btn-status">${active ? 'on' : 'off'}</span>`;

  // Wide layouts get the full label bracketing the keyboard shortcut ([S]hape Branch);
  // the narrow circular buttons get a terse label that fits. CSS swaps the two spans on
  // the width breakpoint (.btn-text-full / .btn-text-short), exactly like Grow/Add — so
  // the label tracks the layout width (and resizes live), not touch capability. (Driving
  // it off isTouch() mis-rendered the long desktop label inside the small mobile-layout
  // buttons whenever a non-touch window was narrow.)
  if (shapeButton) {
    const label =
      '<span class="btn-text btn-text-full"><span class="hotkey">S</span>hape Branch</span>' +
      '<span class="btn-text btn-text-short">Shape</span>';
    shapeButton.innerHTML = `<span>🪾</span> ${label}` + status(shaping);
    shapeButton.classList.toggle('active-shape-mode', shaping);
    shapeButton.classList.toggle('mode-inactive', !shaping);
  }
  if (pruneButton) {
    const pruneLabel =
      '<span class="btn-text btn-text-full"><span class="hotkey">P</span>rune</span>' +
      '<span class="btn-text btn-text-short">Prune</span>';
    pruneButton.innerHTML = `<span>✂️</span> ${pruneLabel}` + status(pruning);
    pruneButton.classList.toggle('active-prune-mode', pruning);
    pruneButton.classList.toggle('mode-inactive', !pruning);
  }
}

// Bottom-bar copy for the non-shaping modes (shape mode has its own, richer
// instructions via updateShapingInstructions).
function setBaseInstructions(forPrune) {
  const instructions = document.getElementById('instructions');
  if (!instructions) return;
  let msg;
  if (forPrune) {
    msg = isTouch()
      ? 'Drag to rotate • Pinch to zoom • Tap a branch to prune'
      : 'Click + drag to rotate • Scroll to zoom • Click branches to prune';
  } else {
    msg = isTouch()
      ? 'Drag to rotate • Pinch to zoom • Tap Shape or Prune to edit'
      : 'Click + drag to rotate • Scroll to zoom • Pick Shape or Prune to edit';
  }
  instructions.innerHTML = msg + ' <span class="close-instructions">✕</span>';
  instructions.style.background = 'rgba(0, 0, 0, 0.7)';
}

// Switch edit mode, running the enter/leave cleanup for whichever modes are
// involved, then sync the buttons. Setting the current mode is a no-op.
function setMode(next) {
  if (next === mode) return;
  const prev = mode;
  mode = next;

  // Leaving prune: drop any hover/preview state.
  if (prev === 'prune') {
    if (hoveredBranch) { hoveredBranch.clearCutPreview(); hoveredBranch = null; hoveredCutRing = null; }
    clearMainHover();
    document.body.classList.remove('branch-hover');
  }
  // Leaving shape: tear down any selection/gizmo and the shape-mode body classes.
  if (prev === 'shape') {
    clearShapeSelection();
    document.body.classList.remove('shape-mode');
    document.body.classList.remove('shape-mode-active');
  }

  if (next === 'shape') {
    updateShapingInstructions(false); // Start in normal shaping mode
    document.body.classList.add('shape-mode');
    document.body.classList.remove('shape-mode-active');
  } else {
    setBaseInstructions(next === 'prune');
  }

  refreshModeButtons();
}

// Button handlers: each toggles its own mode, dropping to 'none' ("Exit") when
// it's already active. Right-clicking in shape mode also routes here.
function toggleShapeMode() {
  setMode(mode === 'shape' ? 'none' : 'shape');
}

function togglePruneMode() {
  setMode(mode === 'prune' ? 'none' : 'prune');
}


// Clear branch selection and gizmos
function clearShapeSelection() {
  if (selectedBranch) {
    // Reset branch highlight
    if (selectedBranch.isMainBranch && selectedBranch.mesh) {
      selectedBranch.mesh.material.emissive.setHex(0x000000);
    } else if (selectedBranch.branchMesh) {
      selectedBranch.branchMesh.material.emissive.setHex(0x000000);
    }
  }
  
  if (shapeGizmo) {
    gsap.killTweensOf(shapeGizmo.scale);
    app.scene.remove(shapeGizmo);
    shapeGizmo = null;
  }
  
  selectedBranch = null;
  isDragging = false;
  shapeStartPoint = null;
  if (app.controls) app.controls.enabled = true;  // never leave the camera locked if a grab is torn down mid-bend

  // Reset any custom instruction styling
  const instructions = document.getElementById('instructions');
  if (instructions) {
    instructions.style.background = '';
  }
}

// Create the orange shaping dot at the grabbed point on `branch`, and remember
// that point in the branch's local space so it can ride along as the branch bends.
function createShapeGizmo(position, branch) {
  if (shapeGizmo) {
    app.scene.remove(shapeGizmo);
  }

  const gizmoGeometry = new THREE.SphereGeometry(2, 8, 6);
  const gizmoMaterial = new THREE.MeshBasicMaterial({
    color: 0xff6b35,
    transparent: true,
    opacity: 0.9,
    // Draw on top of everything: skip the depth test (and don't write depth) so
    // foliage and trunk can't occlude the dot, and force it last in render order.
    depthTest: false,
    depthWrite: false
  });

  shapeGizmo = new THREE.Mesh(gizmoGeometry, gizmoMaterial);
  shapeGizmo.renderOrder = 999;
  shapeGizmo.position.copy(position);
  app.scene.add(shapeGizmo);

  // Bind the dot to the branch at the grab point (world → branch-local).
  gizmoTarget = branch ? (branch.isMainBranch ? branch.group : branch.mesh) : null;
  if (gizmoTarget) {
    gizmoTarget.updateWorldMatrix(true, false);
    gizmoLocal.copy(gizmoTarget.worldToLocal(position.clone()));
  }

  // Animate the gizmo to make it noticeable
  gsap.to(shapeGizmo.scale, {
    x: 1.5, y: 1.5, z: 1.5,
    duration: 0.5,
    yoyo: true,
    repeat: -1,
    ease: "power2.inOut"
  });
}

// Re-place the shaping dot at its grab point on the branch's current pose, so it
// stays stuck to the wood as the branch bends. No-op if there's no live gizmo.
function updateGizmoPosition() {
  if (!shapeGizmo || !gizmoTarget) return;
  gizmoTarget.updateWorldMatrix(true, false);
  _gizmoWorld.copy(gizmoLocal);
  gizmoTarget.localToWorld(_gizmoWorld);
  shapeGizmo.position.copy(_gizmoWorld);
}

function onMouseDownShaping(event) {
  if (event.button === 2) { // Right click - exit shaping mode
    event.preventDefault();
    toggleShapeMode();
    return;
  }
  
  if (event.button === 0) { // Left click
    event.preventDefault();
    event.stopPropagation();
    
    // If we're already dragging, release the branch
    if (isDragging) {
      releaseBranch();
      document.body.classList.remove('shape-mode-active');
      return;
    }
    
    // If we have a selected branch and we're not dragging, start shaping
    if (selectedBranch) {
      
      isDragging = true;
      app.controls.enabled = false;  // hold the camera still while bending (re-enabled on release)
      shapeGrabX = event.clientX;    // baseline for the click-vs-drag test on mouseup
      shapeGrabY = event.clientY;
      shapeDidDrag = false;
      snowShedThisGesture = false;   // a fresh grab can shed snow again
      haveShakeDir = false;          // fresh gesture: don't compare against the last drag's direction
      shapeStartPoint = getWorldMousePosition();
      lastShapeScreen.copy(mouse);   // baseline for incremental screen-delta bends
      document.body.classList.add('shape-mode-active');

      // Create gizmo at intersection point
      if (selectedBranch.intersectionPoint) {
        createShapeGizmo(selectedBranch.intersectionPoint.clone(), selectedBranch);
        document.body.classList.add('shape-mode-active');
      }

      // Update instructions to show how to release
      updateShapingInstructions(true);
    }
  }
}

// New function to handle releasing the branch
function releaseBranch() {
  finishShape(); // record the bend before clearing drag state
  isDragging = false;
  shapeStartPoint = null;
  app.controls.enabled = true;   // hand the camera back now the bend is done

  // Remove gizmo with nice animation
  if (shapeGizmo) {
    gsap.killTweensOf(shapeGizmo.scale);
    
    // Animate gizmo disappearing
    gsap.to(shapeGizmo.scale, {
      x: 0, y: 0, z: 0,
      duration: 0.3,
      ease: "back.in(1.7)",
      onComplete: () => {
        if (shapeGizmo) {
          app.scene.remove(shapeGizmo);
          shapeGizmo = null;
        }
      }
    });
  }
  
  // Update instructions back to normal shaping mode
  updateShapingInstructions(false);
  
}

function onMouseUpShaping(event) {
  // Auto-release on the end of a genuine click-and-drag. A grab the player just
  // clicked in place (no real movement) stays held for the click-to-release
  // toggle, so both interaction styles work.
  if (isDragging && shapeDidDrag) {
    releaseBranch();
    document.body.classList.remove('shape-mode-active');
  }
}

// Get world position from mouse coordinates
function getWorldMousePosition() {
  raycaster.setFromCamera(mouse, app.camera);
  
  // Create a plane at the current app.camera distance to project mouse position
  const distance = app.camera.position.distanceTo(app.controls.target);
  const planeNormal = app.camera.getWorldDirection(new THREE.Vector3()).negate();
  const plane = new THREE.Plane(planeNormal, distance);
  
  const worldPoint = new THREE.Vector3();
  raycaster.ray.intersectPlane(plane, worldPoint);
  
  return worldPoint;
}

// Record the final bend of the branch that was just shaped, so a saved tree
// reproduces it on load. Called when a shaping drag ends; no-op unless a bend
// was actually applied (shapeDirty). Main branches rotate their group; regular
// branches rotate their mesh — mirror what shapeBranch() touched.
function finishShape() {
  if (!shapeDirty || !selectedBranch || !app.tree) { shapeDirty = false; return; }
  // (Shaping rustle now plays continuously during the drag — see shapeBranch.
  // This end-of-gesture moment is left free for a future settle/release sound.)
  if (selectedBranch.isMainBranch) {
    app.tree.record({
      op: 'shape', isMain: true,
      id: selectedBranch.group.userData.branchId,
      ry: selectedBranch.group.rotation.y,
      rz: selectedBranch.group.rotation.z
    });
  } else {
    app.tree.record({
      op: 'shape', isMain: false,
      id: selectedBranch.branchId,
      ry: selectedBranch.mesh.rotation.y,
      rz: selectedBranch.mesh.rotation.z
    });
  }
  shapeDirty = false;
}

// Project the branch tip (a representative local point) to NDC screen space at a
// given local (ry, rz). Used to measure how the tip responds to each rotation.
function tipToScreen(target, tipLocal, ry, rz, out) {
  target.rotation.y = ry;
  target.rotation.z = rz;
  target.updateWorldMatrix(true, false);
  _tipWorld.copy(tipLocal).applyMatrix4(target.matrixWorld).project(app.camera);
  return out.set(_tipWorld.x, _tipWorld.y);
}

// Camera-relative bend: turn the branch so its tip moves toward the on-screen
// drag direction, no matter which way the branch points or where the camera is.
//
// The branch has two angular DOF (local rotation.y = azimuth, rotation.z = pitch),
// but how each maps to *screen* motion depends on branch orientation and camera
// angle — which is why a fixed-sign scheme felt inverted for some branches. So we
// build the 2×2 Jacobian J of (screen tip position) w.r.t. (ry, rz) by finite
// differences, then take the gradient g = Jᵀ·(drag): the (ry, rz) direction that
// best moves the tip along the drag (correct sign for any orientation/camera).
//
// We do NOT invert J (the damped-least-squares "tip exactly follows cursor" version
// jittered: for a small/far branch J is tiny, the inverse blows the step up, and it
// thrashes). Instead we step a *fixed angular rate* along the unit gradient, so
// sensitivity is steady regardless of how big the branch looks and there's nothing
// to blow up — near the camera-facing singularity g just shrinks and the unit step
// still carries the branch smoothly through it.
//
// `dragScreen` is the *incremental* per-frame cursor delta in NDC (~[-2,2] across
// the viewport), so bends accumulate frame to frame and rotation is unbounded:
// keep dragging one way and the branch keeps turning, full 360° and beyond (with
// rotation.x fixed at 0 the accumulating y/z never gimbal-lock).
function shapeBranch(branch, dragScreen) {
  if (!branch) return;

  const sx = dragScreen.x, sy = dragScreen.y;
  const sLen = Math.hypot(sx, sy);
  if (sLen < 1e-6) return;   // no cursor movement this frame → nothing to do

  const isMain = branch.isMainBranch;
  const target = isMain ? branch.group : branch.mesh;
  const tip = isMain ? _tipMain : _tipReg;
  // Radians of bend per NDC of drag. Regular branches read smaller on screen, so
  // they get a touch more. ~2 NDC spans the viewport, so a full drag ≈ 230–300°.
  const angRate = (isMain ? 2.0 : 2.6) * parameters.shapeSensitivity;

  const ry = target.rotation.y;
  const rz = target.rotation.z;
  const eps = 0.001;

  tipToScreen(target, tip, ry, rz, _s0);
  tipToScreen(target, tip, ry + eps, rz, _sY);
  tipToScreen(target, tip, ry, rz + eps, _sZ);
  target.rotation.y = ry;   // restore; render loop refreshes the matrix
  target.rotation.z = rz;

  // Jacobian columns: ∂screen/∂ry and ∂screen/∂rz.
  const ay = (_sY.x - _s0.x) / eps, by = (_sY.y - _s0.y) / eps;
  const az = (_sZ.x - _s0.x) / eps, bz = (_sZ.y - _s0.y) / eps;

  // Gradient g = Jᵀ·drag: the rotation direction that moves the tip along the drag.
  const gRy = ay * sx + by * sy;
  const gRz = az * sx + bz * sy;
  const gLen = Math.hypot(gRy, gRz);
  if (gLen < 1e-9) return;   // tip can't move along this drag (dead-on singularity)

  // Step a fixed angular amount (∝ drag length) along the unit gradient.
  const step = angRate * sLen / gLen;
  // Azimuth (rotation.y) is unbounded — spin the branch fully around the trunk.
  // Pitch (rotation.z) keeps the old limits so a branch can't droop down through
  // the pot or fold straight back over itself. Pitch alone sets elevation, so this
  // bounds up/down reach regardless of azimuth.
  target.rotation.y = ry + gRy * step;
  target.rotation.z = THREE.MathUtils.clamp(
    rz + gRz * step,
    -Math.PI / 2,
    isMain ? Math.PI / 4 : Math.PI / 6
  );
  shapeDirty = true;   // a real bend was applied; finishShape() will record it

  // Winter: the first real bend of a gesture knocks the snow off the branch (and
  // its descendants); it re-accumulates slowly afterwards (see updateSnowMesh). Both
  // the bare 'none' tree and the snowy 'evergreen' conifer carry shakeable snow.
  if (!snowShedThisGesture && (parameters.leafType === 'none' || parameters.leafType === 'evergreen')) {
    shedSnowIn(target);
    snowShedThisGesture = true;
  }

  // Rustle *while* bending — throttled so a fast drag (many frames/sec) doesn't
  // machine-gun the sample. Fires only when the branch actually moved. A sharp
  // back-and-forth shake (the drag direction reversing vs the previous frame)
  // gets the punchy hit instead of the steady rustle, on its own shorter cooldown
  // so rapid shaking can pop; a smooth one-way bend stays on the steady rustle.
  const dirX = sx / sLen, dirY = sy / sLen;
  const isShake = haveShakeDir && sLen >= SHAPE_SHAKE_MIN_NDC &&
                  (dirX * shakeDirX + dirY * shakeDirY) <= SHAPE_SHAKE_DOT;
  shakeDirX = dirX; shakeDirY = dirY; haveShakeDir = true;

  // Skip the rustle on a bare winter tree — no leaves to rustle.
  if (parameters.leafType !== 'none') {
    const now = performance.now();
    if (isShake) {
      if (now - lastShapeShake >= SHAPE_SHAKE_MS) {
        lastShapeShake = now;
        playSfx('rustleShake', { rate: SHAPE_RUSTLE_RATE });
      }
    } else if (now - lastShapeRustle >= SHAPE_RUSTLE_MS) {
      lastShapeRustle = now;
      playSfx('rustle');
    }
  }
}

function onMouseMove(event) {
  // If we're in shaping mode, use shaping mouse move instead
  if (mode === 'shape') {
    onMouseMoveShaping(event);
    return;
  }
  // 'none' mode: view-only, so no prune hover/preview.
  if (mode !== 'prune') return;

  function onMouseMoveShaping(event) {
  // Update mouse coordinates
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

  // If we're actively dragging a branch, apply shaping
  if (isDragging && selectedBranch && shapeStartPoint) {
    // Once the cursor leaves the grab spot, treat this as a real drag so a held
    // button auto-releases on mouseup (rather than needing a second click).
    if (!shapeDidDrag &&
        Math.hypot(event.clientX - shapeGrabX, event.clientY - shapeGrabY) > SHAPE_DRAG_PX) {
      shapeDidDrag = true;
    }
    // Incremental screen-space drag since last frame drives the camera-relative bend.
    _drag.set(mouse.x - lastShapeScreen.x, mouse.y - lastShapeScreen.y);
    lastShapeScreen.copy(mouse);
    shapeBranch(selectedBranch, _drag);

    // Keep the gizmo under the cursor as a drag handle.
    updateGizmoPosition();   // keep the dot stuck to the branch as it bends

    return;
  }

  // If not dragging, handle branch selection (hovering)
  raycaster.setFromCamera(mouse, app.camera);
  const intersects = raycaster.intersectObjects(app.scene.children, true);

  // Reset previous selection
  if (selectedBranch && !isDragging) {
    if (selectedBranch.isMainBranch && selectedBranch.mesh) {
      selectedBranch.mesh.material.emissive.setHex(0x000000);
    } else if (selectedBranch.branchMesh) {
      selectedBranch.branchMesh.material.emissive.setHex(0x000000);
    }
    selectedBranch = null;
  }

  // Find new branch to select
  if (intersects.length > 0) {
    let object = intersects[0].object;
    const intersectionPoint = intersects[0].point;
    
    // Check if this is a main branch mesh
    if (object.userData.isMainBranch) {
      selectedBranch = {
        isMainBranch: true,
        mesh: object,
        group: object.userData.branchGroup,
        intersectionPoint: intersectionPoint
      };
      object.material.emissive.setHex(0x444400); // Yellow highlight for shaping
    } else {
      // Find regular branch object
      while (object && !object.userData.branch) {
        object = object.parent;
      }
      
      if (object && object.userData.branch && !object.userData.branch.isPruned) {
        selectedBranch = object.userData.branch;
        selectedBranch.intersectionPoint = intersectionPoint;
        selectedBranch.branchMesh.material.emissive.setHex(0x444400); // Yellow highlight
      }
    }
  }
}

  // Original mouse move logic for normal mode
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

  raycaster.setFromCamera(mouse, app.camera);
  const intersects = raycaster.intersectObjects(app.scene.children, true);

  // Figure out what's under the cursor now: a main branch or a regular branch,
  // each with the ring it would be cut at (partial prune; ring 0 = whole branch).
  let newMain = null;
  let newMainRing = null;
  let newBranch = null;
  let newRing = null;
  if (intersects.length > 0) {
    let object = intersects[0].object;
    if (object.userData.isMainBranch) {
      const group = object.userData.branchGroup;
      const ring = app.tree.cutRingForMainBranch(group, intersects[0].point);
      if (ring != null) { // null = at/past the tip, nothing to cut
        newMain = object;
        newMainRing = ring;
      }
    } else {
      while (object && !object.userData.branch) {
        object = object.parent;
      }
      if (object && object.userData.branch && !object.userData.branch.isPruned) {
        const branch = object.userData.branch;
        const ring = branch.cutRingForPoint(intersects[0].point);
        if (ring != null) { // null = past the tip, nothing to cut
          newBranch = branch;
          newRing = ring;
        }
      }
    }
  }

  // Drop hover state we're leaving.
  if (hoveredBranch && hoveredBranch !== newBranch) {
    hoveredBranch.clearCutPreview();
    hoveredBranch = null;
    hoveredCutRing = null;
  }
  if (hoveredMain && (hoveredMain !== newMain || hoveredMainRing !== newMainRing)) {
    clearMainHover();
  }

  // Apply new hover state.
  if (newMain && !hoveredMain) {
    applyMainHover(newMain, newMainRing);
  }
  if (newBranch) {
    hoveredBranch = newBranch;
    if (newRing !== hoveredCutRing) {
      hoveredCutRing = newRing;
      hoveredBranch.previewCut(newRing); // red-tints only the part past the cut
    }
  }

  document.body.classList.toggle('branch-hover', !!(newMain || newBranch));
}

function onMouseUp(event) {
  if (mode === 'shape') onMouseUpShaping(event);
}

function onMouseDown(event) {
  // If we're in shaping mode, use shaping mouse down instead
  if (mode === 'shape') {
    onMouseDownShaping(event);
    return;
  }
  // 'none' mode: view-only, ignore clicks on branches.
  if (mode !== 'prune') return;

  // Original mouse down logic for pruning (only in prune mode)
  if (event.button === 0) { // Left click
    event.preventDefault();

    // Main branches cut at the hovered ring (ring 0 = whole branch); regular
    // branches cut at the hovered ring. Each edit is recorded so a saved tree
    // replays it on load.
    if (hoveredMain) {
      const branchGroup = hoveredMain.userData.branchGroup;
      const k = hoveredMainRing;
      clearMainHover(); // drop the preview before the geometry changes under it
      playSfx('saw'); // saw through it, then the branch's foliage drops once cut (skip on a bare winter tree)
      if (parameters.leafType !== 'none') playSfx('rustleDropMain', { delay: 1.1 });
      if (k === 0) {
        app.tree.pruneMainBranch(branchGroup);
        app.tree.record({ op: 'pruneMain', id: branchGroup.userData.branchId });
      } else {
        app.tree.beginRecordedAction(); // seed so the torn cap's noise replays identically
        app.tree.pruneMainBranchAt(branchGroup, k);
        app.tree.record({ op: 'pruneMain', id: branchGroup.userData.branchId, ring: k });
      }
      document.body.classList.remove('branch-hover');
    } else if (hoveredBranch && !hoveredBranch.isPruned) {
      // Lop off just the part past the cursor (the red-highlighted segment(s)).
      app.tree.beginRecordedAction(); // seed so the cut's bark noise replays identically
      playSfx('shears'); // shears, then foliage drops a beat later (skip on a bare winter tree)
      if (parameters.leafType !== 'none') playSfx('rustleDrop', { delay: 0.35 });
      hoveredBranch.cutAt(hoveredCutRing);
      app.tree.record({ op: 'cut', id: hoveredBranch.branchId, ring: hoveredCutRing });
      hoveredBranch = null;
      hoveredCutRing = null;
      document.body.classList.remove('branch-hover');
    }
  }
}

// --- Touch input (mobile) -------------------------------------------------
// Touch has no hover, so these resolve a target from an explicit screen point
// instead of relying on the mouse-move hover state. The mouse path above is
// left untouched.

// Normalize a client (px) coordinate into the [-1,1] device space the
// raycaster expects, writing into the shared `mouse` vector.
function setMouseFromClient(x, y) {
  mouse.x = (x / window.innerWidth) * 2 - 1;
  mouse.y = -(y / window.innerHeight) * 2 + 1;
}

// Fat-finger tolerance for touch picking. A bare fingertip can't reliably land a
// single ray on a thin/far branch, so when an exact tap misses we sweep a couple
// of rings of sample rays around the touch point (CSS px) and take the frontmost
// branch found. 44px is the common min touch target, so ~22px radius ≈ a fingertip.
const PICK_RADIUS_PX = 22;     // how far around the finger we still grab a branch
const PICK_RINGS = 2;          // concentric sample rings (inner first)
const PICK_RING_SAMPLES = 8;   // rays per ring

// Resolve a raw raycast intersection to the prune/shape target it belongs to —
// a main-branch mesh, or the Branch behind any sub-branch wood/foliage — or null
// if it's not a (live) branch. Carries the hit `point` for ring/gizmo math.
function branchHitFor(intersect) {
  let object = intersect.object;
  if (object.userData.isMainBranch) {
    return { isMain: true, object, group: object.userData.branchGroup, point: intersect.point };
  }
  while (object && !object.userData.branch) object = object.parent;
  if (object && object.userData.branch && !object.userData.branch.isPruned) {
    return { isMain: false, branch: object.userData.branch, point: intersect.point };
  }
  return null;
}

// Cast one ray through a screen point and return the nearest branch hit (with its
// camera distance, for picking the frontmost across samples). Returns { trunk:true }
// if the solid trunk is the frontmost tree part — it occludes branches behind/around
// it so a tap on the trunk doesn't prune through it. Null if it hits neither.
function branchHitAt(x, y) {
  setMouseFromClient(x, y);
  raycaster.setFromCamera(mouse, app.camera);
  const intersects = raycaster.intersectObjects(app.scene.children, true);
  for (const it of intersects) {
    const hit = branchHitFor(it);
    if (hit) return { hit, distance: it.distance };
    if (it.object.userData && it.object.userData.isTrunk) return { trunk: true };
  }
  return null;
}

// Forgiving touch pick: an exact tap wins unchanged; only on a miss do we widen to
// rings of samples and return the frontmost branch in the innermost ring that hits.
function pickBranchNear(x, y) {
  const center = branchHitAt(x, y);
  // Dead-center on the trunk: it occludes, so don't fan out and grab a nearby
  // branch base (they all radiate from the trunk). The tap simply isn't a prune.
  if (center) return center.trunk ? null : center.hit;

  let best = null;
  for (let ring = 1; ring <= PICK_RINGS; ring++) {
    const r = (PICK_RADIUS_PX * ring) / PICK_RINGS;
    for (let i = 0; i < PICK_RING_SAMPLES; i++) {
      // Stagger each ring's angles so the two rings don't sample the same bearings.
      const a = ((i / PICK_RING_SAMPLES) + ring * 0.5) * Math.PI * 2;
      const cand = branchHitAt(x + Math.cos(a) * r, y + Math.sin(a) * r);
      // Only real branch hits count here; trunk samples don't become a target.
      if (cand && cand.hit && (!best || cand.distance < best.distance)) best = cand;
    }
    if (best) break; // innermost ring with a hit wins — don't reach past it
  }
  return best ? best.hit : null;
}

// Prune whatever branch is directly under a screen point — the touch
// equivalent of the hover-then-click prune. Main branches prune whole (same
// GSAP shrink as onMouseDown); regular branches cut at the ring nearest the hit.
function pruneAtPoint(x, y) {
  const hit = pickBranchNear(x, y);
  if (!hit) return;

  if (hit.isMain) {
    const branchGroup = hit.group;
    const k = app.tree.cutRingForMainBranch(branchGroup, hit.point);
    if (k == null) return; // tapped at/past the tip — nothing to remove
    playSfx('saw'); // saw through it, then the branch's foliage drops once cut (skip on a bare winter tree)
    if (parameters.leafType !== 'none') playSfx('rustleDropMain', { delay: 1.1 });
    if (k === 0) {
      app.tree.pruneMainBranch(branchGroup);
      app.tree.record({ op: 'pruneMain', id: branchGroup.userData.branchId });
    } else {
      app.tree.beginRecordedAction(); // seed so the torn cap's noise replays identically
      app.tree.pruneMainBranchAt(branchGroup, k);
      app.tree.record({ op: 'pruneMain', id: branchGroup.userData.branchId, ring: k });
    }
    return;
  }

  const branch = hit.branch;
  const ring = branch.cutRingForPoint(hit.point);
  if (ring != null) {
    app.tree.beginRecordedAction(); // seed so the cut's bark noise replays identically
    playSfx('shears'); // shears, then foliage drops a beat later (skip on a bare winter tree)
    if (parameters.leafType !== 'none') playSfx('rustleDrop', { delay: 0.35 });
    branch.cutAt(ring);
    app.tree.record({ op: 'cut', id: branch.branchId, ring });
  }
}

// True if the frontmost tree part under a screen point is the trunk (not a branch
// in front of it). Walks the hits front-to-back: a live branch wins the tap first
// (it's prunable), so only a clear trunk hit counts.
function tapHitsTrunk(x, y) {
  setMouseFromClient(x, y);
  raycaster.setFromCamera(mouse, app.camera);
  const intersects = raycaster.intersectObjects(app.scene.children, true);
  for (const it of intersects) {
    if (it.object.userData && it.object.userData.isTrunk) return true;
    if (branchHitFor(it)) return false; // a branch sits in front → not a trunk tap
  }
  return false;
}

// Count a tap toward the rage-tap-the-trunk shortcut. A burst of trunk taps in the
// same spot opens the Trunk sheet; any non-trunk tap (or too slow/too far) restarts.
function registerTrunkTap(x, y) {
  if (!tapHitsTrunk(x, y)) { rageTapCount = 0; return; }

  const now = Date.now();
  const inTime = now - rageTapTime <= RAGE_TAP_WINDOW_MS;
  const inPlace = Math.hypot(x - rageTapX, y - rageTapY) <= RAGE_TAP_RADIUS_PX;
  rageTapCount = (inTime && inPlace) ? rageTapCount + 1 : 1;
  rageTapTime = now;
  rageTapX = x;
  rageTapY = y;

  if (rageTapCount >= RAGE_TAP_COUNT) {
    rageTapCount = 0;
    window.openControlSheet && window.openControlSheet('trunk');
    navigator.vibrate && navigator.vibrate(30); // a little "there you go" buzz
  }
}

// Select the branch under a screen point for shaping (mirrors the hover-select
// in onMouseMoveShaping, but driven by an explicit touch point). Sets
// `selectedBranch` + its yellow highlight + intersectionPoint.
function selectBranchAtPoint(x, y) {
  if (selectedBranch) {
    if (selectedBranch.isMainBranch && selectedBranch.mesh) {
      selectedBranch.mesh.material.emissive.setHex(0x000000);
    } else if (selectedBranch.branchMesh) {
      selectedBranch.branchMesh.material.emissive.setHex(0x000000);
    }
    selectedBranch = null;
  }

  const hit = pickBranchNear(x, y);
  if (!hit) return;

  if (hit.isMain) {
    selectedBranch = {
      isMainBranch: true,
      mesh: hit.object,
      group: hit.group,
      intersectionPoint: hit.point
    };
    hit.object.material.emissive.setHex(0x444400);
  } else {
    selectedBranch = hit.branch;
    selectedBranch.intersectionPoint = hit.point;
    selectedBranch.branchMesh.material.emissive.setHex(0x444400);
  }
}

function onTouchStart(event) {
  if (event.touches.length !== 1) return; // two-finger gestures belong to OrbitControls (pinch/pan)

  // Suppress the synthetic mouse events (mousemove/mousedown/click) the browser
  // would fire ~300ms after a tap — they'd re-trigger the mouse prune path and
  // double-handle the gesture. OrbitControls uses Pointer events, which are
  // unaffected by this, so rotate/pinch still work.
  event.preventDefault();

  const t = event.touches[0];
  touchStartX = t.clientX;
  touchStartY = t.clientY;
  touchStartTime = Date.now();
  touchMoved = false;
  touchShaping = false;

  if (mode !== 'shape') return;

  // Shape mode: grab a branch under the finger and start a bend, disabling
  // OrbitControls so the one-finger drag bends instead of rotating the camera.
  selectBranchAtPoint(t.clientX, t.clientY);
  if (!selectedBranch) return;

  touchShaping = true;
  isDragging = true;
  snowShedThisGesture = false;   // a fresh grab can shed snow again
  haveShakeDir = false;          // fresh gesture: don't compare against the last drag's direction
  app.controls.enabled = false;

  setMouseFromClient(t.clientX, t.clientY);
  shapeStartPoint = getWorldMousePosition();
  lastShapeScreen.copy(mouse);   // baseline for incremental screen-delta bends

  if (selectedBranch.intersectionPoint) {
    createShapeGizmo(selectedBranch.intersectionPoint.clone(), selectedBranch);
  }
  updateShapingInstructions(true);
}

function onTouchMove(event) {
  if (event.touches.length !== 1) return;

  const t = event.touches[0];
  if (Math.hypot(t.clientX - touchStartX, t.clientY - touchStartY) > TAP_MOVE_PX) {
    touchMoved = true;
  }

  if (!touchShaping || !selectedBranch || !shapeStartPoint) return;

  event.preventDefault();
  setMouseFromClient(t.clientX, t.clientY);
  // Incremental screen-space drag since last frame drives the camera-relative bend.
  _drag.set(mouse.x - lastShapeScreen.x, mouse.y - lastShapeScreen.y);
  lastShapeScreen.copy(mouse);
  shapeBranch(selectedBranch, _drag);

  updateGizmoPosition();   // keep the dot stuck to the branch as it bends
}

function onTouchEnd() {
  if (touchShaping) {
    finishShape(); // record the bend before clearing drag state
    touchShaping = false;
    app.controls.enabled = true;
    clearShapeSelection();   // resets highlight, removes gizmo, clears drag state
    updateShapingInstructions(false);
    return;
  }

  // A short, near-stationary touch is a tap.
  const dt = Date.now() - touchStartTime;
  if (touchMoved || dt >= TAP_TIME_MS) return;

  if (mode === 'prune') pruneAtPoint(touchStartX, touchStartY);
  // A tap that lands on the bare trunk does nothing above; track the burst so
  // rage-tapping the trunk opens the Trunk sheet instead.
  registerTrunkTap(touchStartX, touchStartY);
}

// Attach the pointer listeners. Called from init() after the renderer exists.
// ----- Keyboard shortcuts ---------------------------------------------------
// Single-key actions for the common tree ops + view toggles; Escape backs out
// of whatever layer is active. We never hijack typing (the tree-name field) and
// leave browser/OS chords alone, except an explicit Cmd/Ctrl+S to save.
// Only text-entry fields should swallow bare-key shortcuts. Checkboxes, radios,
// range sliders, buttons, etc. keep focus after a click (e.g. the settings-drawer
// toggles), so treating every INPUT as "typing" would silently kill the shortcuts
// after any such click. Block on TEXTAREA/SELECT/contentEditable, but for INPUT
// only on the text-like types.
const TEXT_INPUT_TYPES = new Set([
  'text', 'search', 'email', 'url', 'tel', 'password', 'number',
  'date', 'datetime-local', 'month', 'week', 'time',
]);
function isTypingTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable) return true;
  if (tag === 'INPUT') return TEXT_INPUT_TYPES.has((el.type || 'text').toLowerCase());
  return false;
}

function onKeyDown(e) {
  // Cmd/Ctrl+S → save (and swallow the browser's "save page" dialog).
  if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 's' || e.key === 'S')) {
    e.preventDefault();
    window.saveBonsai?.();
    return;
  }

  // Everything below is a bare keypress: skip when typing or holding a modifier.
  if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;

  // Escape unwinds the topmost layer in turn: gallery → settings → zen → edit mode.
  if (e.key === 'Escape') {
    if (document.getElementById('galleryModal')?.style.display === 'flex') {
      window.toggleGallery?.();
    } else if (document.getElementById('settingsDrawer')?.classList.contains('open')) {
      window.toggleSettings?.();
    } else if (document.body.classList.contains('zen-mode')) {
      window.exitZenMode?.();
    } else if (mode !== 'none') {
      setMode('none');
    }
    return;
  }

  switch (e.key.toLowerCase()) {
    case 'n': window.generateNewTree?.(); break;   // New tree
    case 'b': window.growMoreBranches?.(); break;   // grow more Branches
    case 'm': window.mainBranches?.(); break;       // Main branches
    case 's': toggleShapeMode(); break;             // Shape mode
    case 'p': togglePruneMode(); break;             // Prune mode
    case 'g': window.toggleGallery?.(); break;      // Gallery
    case ',': window.toggleSettings?.(); break;     // Settings (the usual "prefs" key)
    case 'f': window.toggleFullscreen?.(); break;   // Fullscreen
    case 'z':                                        // Zen mode (toggle)
      if (document.body.classList.contains('zen-mode')) window.exitZenMode?.();
      else window.enterZenMode?.();
      break;
    default: return;
  }
  e.preventDefault();
}

export function initInteraction() {
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('keydown', onKeyDown);
  // A <select> keeps focus after you click-commit a choice, so isTypingTarget()
  // would keep swallowing the bare-key shortcuts until you clicked elsewhere (even
  // with the drawer closed). Drop focus off the select on a *pointer*-driven change.
  // Keyboard changes are left alone: arrow keys fire change per keypress (blurring
  // would break cycling) and keyboard users Tab away on their own, so focus order
  // and select typeahead stay intact.
  let pointerSelect = false;
  document.addEventListener('pointerdown', e => {
    pointerSelect = e.target?.tagName === 'SELECT';
  }, true);
  document.addEventListener('change', e => {
    if (e.target?.tagName === 'SELECT' && pointerSelect) {
      pointerSelect = false;
      e.target.blur();
    }
  });
  app.renderer.domElement.addEventListener('contextmenu', e => e.preventDefault());
  app.renderer.domElement.addEventListener('mousedown', onMouseDown);
  // mouseup on window (not the canvas) so a drag that ends off-canvas still releases.
  window.addEventListener('mouseup', onMouseUp);

  // Touch path (mobile). passive:false so we can preventDefault during a bend.
  const canvas = app.renderer.domElement;
  canvas.addEventListener('touchstart', onTouchStart, { passive: false });
  canvas.addEventListener('touchmove', onTouchMove, { passive: false });
  canvas.addEventListener('touchend', onTouchEnd);
}

export { toggleShapeMode, togglePruneMode, refreshModeButtons };
